<template>
  <div class="btn-editor fade-in">

    <h3 class="panel-title">
      <i class="bx bx-pointer"></i> Button Styling
    </h3>
    <p class="panel-sub">Customize look, feel, and interaction</p>

    <!-- Empty State -->
    <div v-if="!selectedElement" class="empty-mini">
      <i class="bx bx-select-multiple"></i>
      <p>Select a button to edit</p>
    </div>

    <!-- Editor UI -->
    <div v-else class="editor-controls">

      <!-- Live Preview -->
      <div class="preview-block">
        <p class="preview-label">Live Preview</p>
        <div class="live-preview">
          <button :style="previewStyle">{{ config.text || "Button" }}</button>
        </div>
      </div>

      <!-- Inputs -->
      <div class="input-group">
        <label>Button Text</label>
        <input type="text" v-model="config.text" @input="apply" class="input-box"/>
      </div>

      <div class="input-row-2col">
        <div>
          <label>Background</label>
          <input type="color" v-model="config.bg" @input="apply" class="color-picker"/>
        </div>
        <div>
          <label>Text Color</label>
          <input type="color" v-model="config.color" @input="apply" class="color-picker"/>
        </div>
      </div>

      <div class="input-row-2col">
        <div>
          <label>Radius</label>
          <input type="range" min="0" max="40" v-model="config.radius" @input="apply"/>
        </div>
        <div>
          <label>Font Size</label>
          <input type="range" min="10" max="26" v-model="config.fontSize" @input="apply"/>
        </div>
      </div>

      <label>Font Weight</label>
      <select v-model="config.weight" @change="apply" class="input-box">
        <option value="400">Normal</option>
        <option value="500">Medium</option>
        <option value="600">Semi-Bold</option>
        <option value="700">Bold</option>
      </select>

      <!-- Padding Controls -->
      <div class="pad-sec">
        <span class="group-label">Padding</span>
        <div class="pad-row">
          <input type="number" v-model.number="config.pv" @input="apply" min="4" max="30"/>
          <span>x</span>
          <input type="number" v-model.number="config.ph" @input="apply" min="8" max="50"/>
        </div>
      </div>

      <!-- Border Controls -->
      <div class="input-row-2col">
        <div>
          <label>Border Style</label>
          <select v-model="config.borderStyle" @change="apply" class="input-box">
            <option value="none">None</option>
            <option value="solid">Solid</option>
            <option value="dashed">Dashed</option>
            <option value="dotted">Dotted</option>
          </select>
        </div>

        <div>
          <label>Border Color</label>
          <input type="color" v-model="config.borderColor" @input="apply" class="color-picker"/>
        </div>
      </div>

      <!-- Hover Effect -->
      <label>Hover Effect</label>
      <select v-model="config.hover" @change="apply" class="input-box">
        <option value="none">None</option>
        <option value="darken">Darken</option>
        <option value="lighten">Lighten</option>
        <option value="pop">Pop (Scale)</option>
      </select>

      <!-- Preset Themes -->
      <div class="preset-sec">
        <p class="preset-title">Quick Themes</p>
        <div class="preset-row">
          <button class="preset primary" @click="applyPreset('primary')"></button>
          <button class="preset dark" @click="applyPreset('dark')"></button>
          <button class="preset outline" @click="applyPreset('outline')"></button>
          <button class="preset gradient" @click="applyPreset('gradient')"></button>
        </div>
      </div>

      <!-- Remove Button -->
      <div class="action-row">
        <button class="btn-reset full ghost" @click="resetButton">
          <i class="bx bx-reset"></i> Reset to Default
        </button>
        <button class="btn-danger full" @click="$emit('removeElement')">
          <i class="bx bx-trash"></i> Remove Button
        </button>
      </div>


    </div>
  </div>
</template>


<script>
export default {
  name: "ButtonEditor",

  props: {
    selectedElement: Object,
  },

  data() {
    return {
      config: {
        text: "Button",
        bg: "#2563eb",
        color: "#ffffff",
        radius: 8,
        fontSize: 14,
        weight: "500",
        pv: 8,
        ph: 18,
        borderStyle: "none",
        borderColor: "#2563eb",
        hover: "none",
      },
      originalConfig: null
    };
  },
  watch: {
    selectedElement: {
      immediate: true,
      handler(el) {
        if (!el) return;

        this.config.text = el.innerText.trim() || "Button";
        this.config.bg = el.style.backgroundColor || "#2563eb";
        this.config.color = el.style.color || "#ffffff";
        this.config.radius = parseInt(el.style.borderRadius) || 8;
        this.config.fontSize = parseInt(el.style.fontSize) || 14;
        this.config.weight = el.style.fontWeight || "500";

        // backup original (deep clone for safety)
        this.originalConfig = JSON.parse(JSON.stringify(this.config));
      }
    }
  },
  computed: {
    previewStyle() {
      return {
        background: this.config.bg,
        color: this.config.color,
        fontWeight: this.config.weight,
        fontSize: this.config.fontSize + "px",
        borderRadius: this.config.radius + "px",
        padding: `${this.config.pv}px ${this.config.ph}px`,
        border: this.config.borderStyle !== "none" ? `2px ${this.config.borderStyle} ${this.config.borderColor}` : "none",
        transition: "0.25s ease",
      };
    }
  },

  methods: {
    apply() {
      this.$emit("updateButton", this.config);
    },
    resetButton() {
      if (!this.originalConfig) return;

      // restore config
      this.config = JSON.parse(JSON.stringify(this.originalConfig));

      // apply restored styling to selected button
      this.$emit("updateButton", this.config);
    },
    applyPreset(type) {
      if (type === "primary") {
        this.config.bg = "#2563eb";
        this.config.color = "#ffffff";
      }
      if (type === "dark") {
        this.config.bg = "#111827";
        this.config.color = "#ffffff";
      }
      if (type === "outline") {
        this.config.bg = "transparent";
        this.config.color = "#111827";
        this.config.borderStyle = "solid";
      }
      if (type === "gradient") {
        this.config.bg = "linear-gradient(135deg,#4f46e5,#06b6d4)";
        this.config.color = "#fff";
      }
      this.apply();
    },
  },
};
</script>

<style scoped>
.btn-editor {
  display: flex;
  flex-direction: column;
  gap: 18px; /* increased spacing for better breathing */
  width: 100%;
  padding-bottom: 6px;
}

/* Header */
.panel-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.panel-sub {
  font-size: 12px;
  color: var(--muted);
  margin-top: -4px;
  line-height: 1.4;
}

/* Live Preview */
.live-preview {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 14px 0;
  border: 1px solid var(--border);
  background: var(--card);
  border-radius: 8px;
  margin-bottom: 2px;
}

.live-preview button {
  border: none;
  cursor: pointer;
  transition: .25s ease;
}

/* Label styling */
label {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--text);
  margin-bottom: 4px;
  display: block;
}

/* Inputs & Select */
.input-box,
select {
  padding: 9px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  width: 100%;
  font-size: 13px;
  background: #fff;
  transition: .22s ease;
  margin-bottom: 6px;
}

.input-box:focus,
select:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.2);
}

/* Color Pickers */
.color-picker {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  border: 1px solid var(--border);
  cursor: pointer;
  padding: 0;
  margin-bottom: 6px;
}

/* Range input spacing */
input[type='range'] {
  width: 100%;
  margin: 6px 0 10px 0;
}

/* Padding Controls */
.pad-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  margin-bottom: 6px;
}

.pad-row input {
  flex: 1;
  padding: 8px 6px;
  border: 1px solid var(--border);
  border-radius: 8px;
  text-align: center;
}

.pad-row span {
  font-size: 13px;
  color: var(--muted);
}

/* Preset Section */
.preset-sec {
  margin-top: 4px;
}

.preset-sec p {
  font-size: 12.5px;
  font-weight: 500;
  color: var(--muted);
  margin-bottom: 8px;
}

.preset-row {
  display: flex;
  gap: 10px;
  margin-bottom: 6px;
}

.preset {
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: none;
  cursor: pointer;
  transition: .22s ease;
}

.preset:hover {
  transform: scale(1.07);
}

/* Preset Colors */
.preset.primary { background: #2563eb; }
.preset.dark { background: #111827; }
.preset.outline {
  background: transparent;
  border: 2px solid #111827;
}
.preset.gradient {
  background: linear-gradient(135deg, #4f46e5, #06b6d4);
}

/* Remove Button */
.full {
  width: 100%;
}

.mt10 {
  margin-top: 14px;
}

.empty-mini {
  border: 1px dashed var(--border);
  padding: 28px 20px;
  text-align: center;
  border-radius: 10px;
  color: var(--muted);
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
}

/* Smooth Hover Animation */
button,
input,
select {
  transition: .22s ease;
}

.btn-editor {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Header */
.panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--text);
}

.panel-sub {
  font-size: 12.3px;
  color: var(--muted);
  margin-top: -4px;
  margin-bottom: 4px;
}

/* Empty state */
.empty-mini {
  border: 1px dashed var(--border);
  border-radius: 10px;
  padding: 30px 16px;
  text-align: center;
  color: var(--muted);
  font-size: 13px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Live preview box */
.preview-block {
  margin-top: 4px;
}

.preview-label {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 4px;
}

.live-preview {
  border: 1px solid var(--border);
  background: var(--card);
  padding: 18px 0;
  display: flex;
  justify-content: center;
  border-radius: 8px;
}

/* Group Layouts */
.input-group,
.input-row-2col,
.pad-sec {
  width: 100%;
}

.input-row-2col {
  display: flex;
  gap: 10px;
  align-items: center;
}

.input-row-2col > div {
  flex: 1;
}



/* Labels */
label,
.group-label {
  font-size: 12.4px;
  font-weight: 500;
  color: var(--text);
  display: block;
  margin-bottom: 4px;
}

/* Inputs & Select */
.input-box,
select,
input[type="number"] {
  width: 100%;
  padding: 9px 11px;
  border: 1px solid var(--border);
  background: white;
  border-radius: 8px;
  font-size: 13px;
}

.input-box:focus,
select:focus {
  border-color: var(--primary);
}

/* Range */
input[type="range"] {
  width: 100%;
}

/* Color picker */
.color-picker {
  width: 100%;
  border: 1px solid var(--border);
  border-radius: 6px;
  padding: 0;
  height: 35px;
}

/* Padding input row */
.pad-row {
  display: flex;
  align-items: center;
  gap: 10px;
}
.pad-row input {
  flex: 1;
  text-align: center;
}


/* Preset buttons */
.preset-sec {
  margin-top: 4px;
}
.preset-title {
  font-size: 12.5px;
  color: var(--text);
  margin-bottom: 6px;
  opacity: .7;
}
.preset-row {
  display: flex;
  gap: 10px;
}
.preset {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  border: none;
}

/* Preset colors */
.preset.primary { background: #2563eb; }
.preset.dark { background: #111827; }
.preset.outline { border: 2px solid #111827; background: transparent; }
.preset.gradient { background: linear-gradient(135deg,#4f46e5,#06b6d4); }

.mt10 { margin-top: 12px; }
.full { width: 100%; }
.action-row {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 6px;
}

/* Reset Button */
.btn-reset {
  background: #fafafa;
  border: 1px solid var(--border);
  color: var(--text);
  border-radius: 8px;
  padding: 10px;
  font-size: 13px;
  cursor: pointer;
  transition: .22s ease;
}

.btn-reset:hover {
  background: #f0f0f0;
  transform: translateY(-1px);
}

</style>

